import { type PropsWithChildren, type ReactElement, useMemo } from 'react';

import type {
  ConvertibleUnit,
  FormatOptions,
  Unit,
} from '@dynatrace-sdk/units';

import { StoreProvider } from './StoreProvider.js';
import { OverlayProvider } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ChartSeriesActionContext } from '../../../core/components/series-action/ChartSeriesActionContext.js';
import { ColorRuleContext } from '../../../core/context/ColorRuleContext.js';
import type {
  ColorPalette,
  CustomColorPalette,
} from '../../../core/types/color-palette.js';
import type { ColorRuleProps } from '../../../core/types/color-rule.js';
import type { Formatter } from '../../../core/types/formatter.js';
import { SeriesActionsTemplate } from '../../../core/types/series-actions-template.js';
import { FormatterContext } from '../../shared/context/FormatterContext.js';
import { MeterBarChartLoadingContext } from '../../shared/context/MeterBarChartLoadingContext.js';
import { MeterBarToolbarConfigContext } from '../../shared/context/MeterBarToolbarConfigContext.js';
import type { MeterBarToolbarInternalProps } from '../../shared/types/meter-bar-toolbar.js';
import type {
  MultiMeterBarSegmentData,
  MultiMeterBarSegmentProps,
} from '../types/multi-meter-bar-chart.js';

interface MultiMeterBarChartProvidersProps {
  colorPalette: ColorPalette | CustomColorPalette;
  formatter: Formatter | FormatOptions<Unit, ConvertibleUnit>;
  unit?: string | Unit;
  segments: MultiMeterBarSegmentProps[];
  colorRules: ColorRuleProps[];
  toolbar: MeterBarToolbarInternalProps;
  loading: boolean;
  seriesActions?: (
    seriesData: MultiMeterBarSegmentData,
  ) => SeriesActionsTemplate;
}

export const MultiMeterBarChartProviders = ({
  colorPalette,
  formatter,
  unit,
  segments,
  colorRules,
  children,
  toolbar,
  seriesActions,
  loading,
}: PropsWithChildren<MultiMeterBarChartProvidersProps>) => {
  const formatterContextProps = useMemo(
    () => ({
      formatter,
      unit,
    }),
    [formatter, unit],
  );
  const contextSeriesActionsValue = useMemo(
    () => ({
      chartSeriesActions: seriesActions as (
        series: unknown,
        datapoint?: unknown,
      ) => ReactElement<PropsWithChildren>,
    }),
    [seriesActions],
  );

  return (
    <ChartSeriesActionContext.Provider value={contextSeriesActionsValue}>
      <FormatterContext.Provider value={formatterContextProps}>
        <ColorRuleContext.Provider value={colorRules}>
          <MeterBarChartLoadingContext.Provider value={loading}>
            <MeterBarToolbarConfigContext.Provider value={toolbar}>
              <StoreProvider
                colorPalette={colorPalette}
                segmentsSlot={segments}
              >
                <OverlayProvider>
                  <TooltipProvider>{children}</TooltipProvider>
                </OverlayProvider>
              </StoreProvider>
            </MeterBarToolbarConfigContext.Provider>
          </MeterBarChartLoadingContext.Provider>
        </ColorRuleContext.Provider>
      </FormatterContext.Provider>
    </ChartSeriesActionContext.Provider>
  );
};
